import { createSelector } from 'reselect';
import has from 'lodash/has';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import intersection from 'lodash/intersection';
import defaults from 'lodash/defaults';
import hasIn from 'lodash/hasIn';
import { TIME_FORMAT } from '../constants';

export const currentUserIsAdmin = state => (
  state.receivedAdmins && state.loggedIn && (state.admins.indexOf(state.userUID) !== -1)
);

export const adminAreaLoaded = (state) => {
  const { fetchedAnswers, fetchedSite } = state;
  let fetchedCurrentLoggedInUser = false;

  if (state.loggedIn === true && !isEmpty(state.userUID) && has(state.users, state.userUID)) {
    fetchedCurrentLoggedInUser = true;
  }

  return fetchedAnswers && fetchedCurrentLoggedInUser && fetchedSite;
};

export const getPosts = state => state.posts;

export const getUsers = (state) => {
  const normalizedUsers = {};

  Object.keys(state.users).forEach((userUID) => {
    normalizedUsers[userUID] = defaults(state.users[userUID], {
      firstName: '',
      lastName: '',
      city: '',
      eventDay: '',
      group: '',
    });
  });

  return normalizedUsers;
};

export const getUserUID = (state, props) => {
  if (hasIn(props, 'userUID')) {
    return props.userUID;
  }

  return state.userUID;
};

export const getUser = createSelector(
  [getUsers, getUserUID], (users, uid) => users[uid],
);


export const getCurrentUser = (state) => {
  let currentUserName = '';
  Object.keys(state.users)
    .forEach((userID) => {
      if (userID === state.userUID) {
        currentUserName = `${state.users[userID].firstName} ${state.users[userID].lastName}`;
      }
    });
  return currentUserName;
};

export const getCurrentUserFirstName = (state) => {
  let currentUserFirstName = '';
  Object.keys(state.users)
    .forEach((userID) => {
      if (userID === state.userUID) {
        currentUserFirstName = state.users[userID].firstName;
      }
    });
  return currentUserFirstName;
};

export const getCurrentUserLastName = (state) => {
  let currentUserLastName = '';
  Object.keys(state.users)
    .forEach((userID) => {
      if (userID === state.userUID) {
        currentUserLastName = state.users[userID].lastName;
      }
    });
  return currentUserLastName;
};

export const getCurrentUserEmail = (state) => {
  let currentUserEmail = '';
  Object.keys(state.users)
    .forEach((userID) => {
      if (userID === state.userUID) {
        currentUserEmail = state.users[userID].email;
      }
    });
  return currentUserEmail;
};

export const getCurrentUserCity = (state) => {
  let currentUserCity = '';
  Object.keys(state.users)
    .forEach((userID) => {
      if (userID === state.userUID) {
        currentUserCity = state.users[userID].city;
      }
    });
  return currentUserCity;
};

export const getCurrentUserGroup = (state) => {
  let currentUserGroup = '';
  Object.keys(state.users)
  .forEach((userID) => {
    if (userID === state.userUID) {
      currentUserGroup = state.users[userID].group;
    }
  });
  return currentUserGroup;
};


export const getQuestions = (state) => {
  const questions = {};
  Object.keys(state.posts)
    .filter(postID => state.posts[postID].postType === 'question')
    .forEach((questionID) => {
      let answers = [];
      let title = '';
      let correctAnswer = '';
      let successMessage = 'Correct!';

      if (has(state.posts, `${questionID}.name`)) {
        title = state.posts[questionID].name;
      }

      if (has(state.posts[questionID], 'answers.correctAnswerMsg')) {
        successMessage = state.posts[questionID].answers.correctAnswerMsg;
      }

      if (has(state.posts, `${questionID}.answers.answer`)) {
        answers = state.posts[questionID].answers.answer.map((answer) => {
          const formattedAnswer = (has(answer, 'answerText') ? answer.answerText : '[Missing]');

          if (answer.correctAnswer === true) {
            correctAnswer = formattedAnswer;
          }

          return formattedAnswer;
        });
      }

      questions[questionID] = {
        title,
        answers,
        correctAnswer,
        successMessage,
      };
    });
  return questions;
};

export const getAnswers = state => state.answers;

export const getQuizSectionIndex = (state, ownProps) => ownProps.match.params.quizSection;

export const getQuestionID = (state, ownProps) => {
  if (hasIn(ownProps, 'questionID')) {
    return ownProps.questionID;
  }

  return ownProps.match.params.questionID;
};

export const getQuizID = createSelector([getPosts], (posts) => {
  let quizID = null;

  const quizzes = Object.keys(posts).filter(postID => posts[postID].postType === 'quiz');

  if (quizzes.length > 0) {
    [quizID] = quizzes;
  }

  return quizID;
});

export const getQuiz = createSelector([getPosts, getQuizID], (posts, quizID) => {
  if (has(posts, quizID)) {
    return posts[quizID];
  }

  return null;
});

export const getUserAnswers = createSelector(
  [getQuestions, getAnswers, getUserUID],
  (questions, answers, userUID) => {
    const userAnswers = {};

    if (!isEmpty(userUID) && hasIn(answers, userUID)) {
      const validQuestionIDs = Object.keys(questions);
      Object.keys(answers[userUID]).forEach((questionID) => {
        if (validQuestionIDs.indexOf(questionID) !== -1) {
          userAnswers[questionID] = answers[userUID][questionID];
        }
      });
    }

    return userAnswers;
  },
);


export const getQuizSections = createSelector([getQuiz, getUserAnswers], (quiz, userAnswers) => {
  let sections = [];

  if (has(quiz, 'quizSections.quizSection')) {
    sections = quiz.quizSections.quizSection.map(quizSection => ({
      title: quizSection.quizSectionTitle,
      questions: quizSection.quizSectionQuestions.map(question => question.questionsSelector),
    }));
  }

  sections.forEach((quizSection, index) => {
    let numAnswered = 0;
    quizSection.questions.forEach((questionID) => {
      if (hasIn(userAnswers, questionID)) {
        numAnswered += 1;
      }
    });

    sections[index].numAnswered = numAnswered;
  });

  return sections;
});


export const getQuizSection = createSelector(
  [getQuizSections, getQuizSectionIndex],
  (sections, sectionIndex) => {
    const section = Object.assign({
      title: '',
      questions: [],
    }, sections[sectionIndex]);

    return section;
  },
);

export const getSectionName = createSelector(
  [getQuizSection],
  section => section.title,
);

export const getUserAnswer = createSelector(
  [getQuestionID, getUserAnswers],
  (questionID, userAnswers) => {
    if (!hasIn(userAnswers, questionID)) {
      return '';
    }

    return userAnswers[questionID];
  },
);

export const userHasAnsweredQuestion = createSelector(
  [getUserAnswer],
  answer => answer !== '',
);

export const getQuestion = createSelector(
  [getQuestionID, getQuestions, getUserAnswer, userHasAnsweredQuestion],
  (questionID, questions, userAnswer, hasAnswered) => {
    const normalizedQuestion = questions[questionID];

    if (!has(normalizedQuestion, 'correctAnswer') || normalizedQuestion.correctAnswer === null) {
      normalizedQuestion.correctAnswer = '';
    }

    normalizedQuestion.userAnswer = userAnswer;
    normalizedQuestion.userAnsweredCorrectly = (hasAnswered && (normalizedQuestion.correctAnswer === userAnswer));
    normalizedQuestion.userHasAnswered = hasAnswered;

    return normalizedQuestion;
  },
);

export const getQuizSectionQuestions = createSelector([getQuizSection], (quizSection) => {
  if (!has(quizSection, 'questions')) {
    return [];
  }

  return quizSection.questions;
});

export const getQuizSectionQuestionObjects = createSelector(
  [getQuizSectionQuestions, getQuestions],
  (quizSectionQuestionIDs, questions) => {
    const objects = {};

    quizSectionQuestionIDs.forEach((questionID) => {
      if (has(questions, questionID)) {
        objects[questionID] = questions[questionID];
      }
    });

    return objects;
  },
);

export const getQuestionNumber = createSelector(
  [
    getQuizSectionQuestions,
    getQuestionID,
  ],
  (questions, questionID) => (questions.indexOf(questionID)),
);

export const getNextLinkTo = createSelector(
  [getQuizSectionIndex, getQuizSectionQuestions, getQuestionNumber],
  (quizSectionIndex, questions, questionNumber) => {
    if (questionNumber === (questions.length - 1)) {
      return `/quiz/${quizSectionIndex}`;
    }

    return `/quiz/${quizSectionIndex}/${questions[questionNumber + 1]}`;
  },
);

export const currentUserNumberQuestionsAnswered = createSelector([
  getAnswers,
  getQuestions,
  getUserUID,
], (answers, questions, uid) => {
  if (uid === null || !answers[uid]) {
    return 0;
  }

  return intersection(Object.keys(answers[uid]), Object.keys(questions)).length;
});

export const totalNumberQuestions = createSelector([
  getQuestions,
], questions => Object.keys(questions).length);

export const currentUserNumberQuestionsAnsweredIncorrectly = createSelector([
  getQuestions,
  getAnswers,
  getUserUID,
], (questions, answers, uid) => {
  if (uid === null || !answers[uid]) {
    return 0;
  }

  let numAnsweredIncorrectly = 0;
  Object.keys(answers[uid]).forEach((questionID) => {
    if (hasIn(questions, `${questionID}.correctAnswer`)) {
      if (answers[uid][questionID] !== questions[questionID].correctAnswer) {
        numAnsweredIncorrectly += 1;
      }
    }
  });

  return numAnsweredIncorrectly;
});

export const currentUserNumberQuestionsAnsweredCorrectly = createSelector([
  currentUserNumberQuestionsAnswered,
  currentUserNumberQuestionsAnsweredIncorrectly,
], (numAnswered, numAnsweredIncorrectly) => numAnswered - numAnsweredIncorrectly);


export const getQuizQuestionIDs = createSelector(
  [getQuizSections],
  (quizSections) => {
    const quizQuestionIDs = [];

    quizSections.forEach((section) => {
      section.questions.forEach((questionID) => {
        if (!hasIn(questionID, quizQuestionIDs)) {
          quizQuestionIDs.push(questionID);
        }
      });
    });

    return quizQuestionIDs;
  },
);

const getUserTableFilters = state => state.userTableFilters;

export const getFilteredUserUIDs = createSelector(
  [getUserTableFilters, getUsers],
  (filters, users) => {
    const filteredUserUIDs = [];

    Object.keys(users).forEach((userUID) => {
      let isValid = true;

      if (!isEmpty(filters.search)) {
        if (
          users[userUID].firstName.toLowerCase().indexOf(filters.search.toLowerCase()) === -1
          && users[userUID].lastName.toLowerCase().indexOf(filters.search.toLowerCase()) === -1
          && users[userUID].email.toLowerCase().indexOf(filters.search.toLowerCase()) === -1
        ) {
          isValid = false;
        }
      }

      const selectKeys = ['city', 'group', 'eventDay'];
      selectKeys.forEach((key) => {
        if (filters[key] === null || filters[key] === undefined) {
          return;
        }

        if (isEmpty(filters[key].toString())) {
          return;
        }

        if (key === 'eventDay') {
          if (users[userUID][key] !== dayjs(parseInt(filters.eventDay, 10)).format(TIME_FORMAT)) {
            isValid = false;
          }
        } else if (users[userUID][key].toString() !== filters[key].toString()) {
          isValid = false;
        }
      });

      if (isValid) {
        filteredUserUIDs.push(userUID);
      }
    });

    return filteredUserUIDs;
  },
);



// WEBPACK FOOTER //
// ./src/selectors/index.js